import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Upload, Loader2, X } from "lucide-react";
import ReactCrop from "react-image-crop";
import "react-image-crop/dist/ReactCrop.css";
import { useQuery, useMutation } from "@tanstack/react-query";
import axios from "axios";
import { toast } from "sonner";
import { BASE_URL } from "utils/constant";
import { useAlert } from "../../components/UI_components/Alert";
import { BsBuilding } from "react-icons/bs";
import { IoHomeOutline } from "react-icons/io5";
import { MdOutlineMoreHoriz } from "react-icons/md";
import { UserAPI } from "api/dashboard/service";
import { ProjectApi } from "api/projects/service";

const stepValidationSchemas = {
  0: z.object({
    site_name: z.string().min(2, "Site name is required"),
    local_admin_body_type: z
      .string()
      .min(1, "Administrative body type is required"),
  }),

  1: z.object({
    site_catagory_id: z.number().min(1, "Building category is required"),
  }),
  2: z.object({
    start_date: z.string().min(1, "Start date is required"),
    constructionStatus: z.enum(["Ongoing", "Completed", "Upcoming", "Halted"]),
    //constructionStage: z.string().optional(),
    image_file: z.instanceof(File).optional(),
  }),
};

const siteSchema = z.object({
  site_name: z.string().min(2, "Site name is required"),
  start_date: z.string().min(1, "Start date is required"),
  constructionStage: z.string().optional(),
  constructionStatus: z.enum(["Ongoing", "Completed", "Upcoming", "Halted"]),
  local_admin_body_type: z
    .string()
    .min(1, "Administrative body type is required"),
  site_catagory_id: z.number().min(1, "Building category is required"),
  image_file: z.instanceof(File).optional(),
  latitude: z.number().optional(),
  longitude: z.number().optional(),
});

const FormLabel = ({ children, required, className = "" }) => (
  <label className={`block text-sm font-medium text-grey-700 mb-1 ${className}`}>
    {children}
    {required && <span className="text-red-500 ml-1">*</span>}
  </label>
);

const FormInput = React.forwardRef(({ error, ...props }, ref) => (
  <input
    ref={ref}
    {...props}
    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 ${
      error ? "border-red-500 focus:ring-red-500" : "border-gray-300"
    } bg-white`}
  />
));

const FormError = ({ children }) =>
  children ? <p className="mt-1 text-sm text-red-500">{children}</p> : null;

const NewSiteForm = ({ onClose }) => {
  const navigate = useNavigate();
  const alert = useAlert();
  const [currentStep, setCurrentStep] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [previewImage, setPreviewImage] = useState(null);
  const [isCropperOpen, setIsCropperOpen] = useState(false);
  const [crop, setCrop] = useState();
  const [completedCrop, setCompletedCrop] = useState(null);
  const [tempImageSrc, setTempImageSrc] = useState(null);
  const [tempImageType, setTempImageType] = useState("image/jpeg");
  const [tempImageName, setTempImageName] = useState("photo.jpg");
  const imgRef = React.useRef(null);
  const token = localStorage.getItem("access");
  const accessToken = token;
  const organization = localStorage.getItem("organization");
  const organizationId = JSON.parse(organization)?.id;

  const form = useForm({
    resolver: zodResolver(siteSchema),
    defaultValues: {
      site_name: "",
      site_area: 0,
      uom: "m_sq",
      address: "",
      city: "",
      pincode: "",
      state: "",
      start_date: "",
      constructionStage: "",
      constructionStatus: "Ongoing",
      local_admin_body_type: "",
      local_admin_body_id: 0,
      local_admin_body_name: "",
      site_catagory_id: 0,
      image_file: undefined,
    },
  });

  // Fetch user details
  const { data: userData } = useQuery({
    queryKey: ["user-details"],
    queryFn: async () => {
      const data = await UserAPI.getUserDetails();
      return data.data || {};
    },
  });

  // Fetch local admin bodies
  const { data: localAdminBodies, isLoading: loadingAdminBodies } = useQuery({
    queryKey: ["local-admin-bodies"],
    // queryFn: async () => {
    //   const { data } = await axios.get(
    //     `${BASE_URL}/sites/get-all-localadminbody/`,
    //     {
    //       headers: { Authorization: `Bearer ${accessToken}` },
    //     }
    //   );
    //   return data.data || [];
    // },
    queryFn: async () => {
      const data = await ProjectApi.getAllLocalBodyAdmin();
      return data.data || [];
    },
    onError: (error) => {
      console.error("Failed to fetch admin bodies:", error);
      toast.error("Failed to load administrative bodies");
    },
  });

  // Fetch site categories
  const { data: siteCategories, isLoading: loadingCategories } = useQuery({
    queryKey: ["site-categories"],
    // queryFn: async () => {
    //   const { data } = await axios.get(
    //     `${BASE_URL}/sites/get-all-sitecatagory/`,
    //     {
    //       headers: { Authorization: `Bearer ${accessToken}` },
    //     }
    //   );
    //   return data.data || [];
    // },
    queryFn: async () => {
      const data = await ProjectApi.getAllSiteCategory();
      return data.data || [];
    },
    onError: (error) => {
      console.error("Failed to fetch site categories:", error);
      toast.error("Failed to load building categories");
    },
  });

  const createSite = useMutation({
    mutationFn: async (formData) => {
      if (!organizationId) {
        throw new Error("Organization ID is not found");
      }
      setIsSubmitting(true);

      try {
        // Create payload as an object instead of FormData
        const payload = {
          organization: Number(organizationId),
          site_name: formData.site_name,
          site_area: Number(formData.site_area) || 0,
          uom: formData.uom,
          address: formData.address,
          city: formData.city,
          pincode: String(formData.pincode),
          state: formData.state,
          start_date: formData.start_date,
          constructionStage: formData.constructionStage || "",
          constructionStatus: formData.constructionStatus,
          local_admin_body_name: String(formData.local_admin_body_name).trim(),
          local_admin_body_id: Number(formData.local_admin_body_id),
          site_catagory_id: Number(formData.site_catagory_id),
          active_flag: true,
        };

        // Handle file upload separately
        const formDataPayload = new FormData();
        Object.keys(payload).forEach((key) => {
          formDataPayload.append(key, payload[key]);
        });

        // Append file if exists
        if (formData.image_file) {
          formDataPayload.append("image_file", formData.image_file);
        }

        const { data } = await axios.post(
          `${BASE_URL}/sites/create-sites/`,
          formDataPayload,
          {
            headers: {
              Authorization: `Bearer ${accessToken}`,
              "Content-Type": "multipart/form-data",
            },
          }
        );
        // const data = await ProjectApi.createSite(formData);
        // console.log("site create API Response:", data);
        if (data.status) {
          alert.addAlert(data.message, "success");
        } else {
          alert.addAlert(data.message, "error");
        }
        return data;
      } catch (error) {
        const errorMessage =
          error.response?.data?.message ||
          error.response?.data?.detail ||
          error.message ||
          "Failed to create site";

        console.log("API Error:", {
          status: error.response?.status,
          data: error.response?.data,
          message: errorMessage,
        });
        alert.addAlert(errorMessage, "error");
        throw new Error(errorMessage);
      } finally {
        setIsSubmitting(false);
      }
    },
    onSuccess: () => {
      toast.success("Site created successfully");
      onClose();
      navigate("/dashboard/projects");
    },
    onError: (error) => {
      toast.error(error.message || "Failed to create site");
    },
  });

  const handleNext = async () => {
    const currentStepFields = Object.keys(
      stepValidationSchemas[currentStep].shape
    );

    const currentStepData = {};
    currentStepFields.forEach((field) => {
      currentStepData[field] = form.getValues(field);
    });
    try {
      await stepValidationSchemas[currentStep].parseAsync(currentStepData);
      if (currentStep < 2) {
        setCurrentStep(currentStep + 1);
      } else {
        const formData = form.getValues();
        if (!formData.local_admin_body_name?.trim()) {
          throw new Error("Local administrative body name is required");
        }

        if (!formData.site_catagory_id) {
          throw new Error("Building category is required");
        }
        createSite.mutate(formData);
      }
    } catch (error) {
      const formattedErrors = {};
      if (error.errors) {
        error.errors.forEach((err) => {
          const field = err.path?.[0];
          if (typeof field === "string") {
            form.setError(field, {
              type: "manual",
              message: err.message,
            });
          }
        });
      }
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    } else {
      onClose();
    }
  };

  const handleUploadPhoto = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setTempImageType(file.type || "image/jpeg");
      setTempImageName(file.name || "photo.jpg");
      const reader = new FileReader();
      reader.onloadend = () => {
        setTempImageSrc(reader.result);
        setIsCropperOpen(true);
      };
      reader.readAsDataURL(file);
      if (e.target) {
        e.target.value = "";
      }
    }
  };

  const handleRemoveImage = () => {
    form.setValue("image_file", undefined);
    setPreviewImage(null);
  };

  const handleAdjustPhoto = () => {
    const currentFile = form.getValues("image_file");
    if (currentFile) {
      setTempImageType(currentFile.type || "image/jpeg");
      setTempImageName(currentFile.name || "photo.jpg");
      const reader = new FileReader();
      reader.onloadend = () => {
        setTempImageSrc(reader.result);
        setIsCropperOpen(true);
      };
      reader.readAsDataURL(currentFile);
    } else if (previewImage) {
      setTempImageType("image/jpeg");
      setTempImageName("photo.jpg");
      setTempImageSrc(previewImage);
      setIsCropperOpen(true);
    }
  };

  const createImage = (url) =>
    new Promise((resolve, reject) => {
      const image = new Image();
      image.addEventListener("load", () => resolve(image));
      image.addEventListener("error", (error) => reject(error));
      image.src = url;
    });

  const getCroppedImg = async (imageSrc, pixelCrop, mimeType = "image/jpeg") => {
    const image = await createImage(imageSrc);
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");

    const displayedWidth = imgRef.current?.width || image.width;
    const displayedHeight = imgRef.current?.height || image.height;
    const naturalWidth = image.naturalWidth || image.width;
    const naturalHeight = image.naturalHeight || image.height;

    const scaleX = naturalWidth / displayedWidth;
    const scaleY = naturalHeight / displayedHeight;

    const cropWidth = Math.max(1, Math.floor(pixelCrop.width * scaleX));
    const cropHeight = Math.max(1, Math.floor(pixelCrop.height * scaleY));
    const sx = Math.max(0, Math.floor(pixelCrop.x * scaleX));
    const sy = Math.max(0, Math.floor(pixelCrop.y * scaleY));

    canvas.width = cropWidth;
    canvas.height = cropHeight;

    ctx.drawImage(
      image,
      sx,
      sy,
      cropWidth,
      cropHeight,
      0,
      0,
      cropWidth,
      cropHeight
    );

    return new Promise((resolve, reject) => {
      canvas.toBlob(
        (blob) => {
          if (!blob) {
            reject(new Error("Canvas is empty"));
            return;
          }
          resolve(blob);
        },
        mimeType,
        0.92
      );
    });
  };

  const handleCropCancel = () => {
    setIsCropperOpen(false);
    setCompletedCrop(null);
    setTempImageSrc(null);
    setCrop(undefined);
  };

  const handleCropConfirm = async () => {
    try {
      const pixelCrop = completedCrop || crop;
      if (!tempImageSrc || !pixelCrop || !pixelCrop.width || !pixelCrop.height) return;
      const blob = await getCroppedImg(tempImageSrc, pixelCrop, tempImageType);
      const croppedFile = new File([blob], tempImageName, { type: tempImageType });
      form.setValue("image_file", croppedFile);

      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewImage(reader.result);
        handleCropCancel();
      };
      reader.readAsDataURL(croppedFile);
    } catch (err) {
      console.error("Cropping failed", err);
      toast.error("Failed to apply crop");
    }
  };

  const onImageLoad = (e) => {
    const { width, height } = e.currentTarget; // displayed dimensions
    setCrop({ unit: "px", x: 0, y: 0, width, height });
    setCompletedCrop({ unit: "px", x: 0, y: 0, width, height });
  };

  // Handle admin body selection
  const handleAdminBodyChange = (id) => {
    const selectedAdminBody = localAdminBodies.find((body) => body.id === id);
    if (selectedAdminBody) {
      form.setValue("local_admin_body_id", selectedAdminBody.id);
      form.setValue("local_admin_body_type", selectedAdminBody.admin_body_type);
    }
  };

  const renderGeneralDetailsForm = () => (
    <div className="space-y-4">
      {/* <h3 className="text-lg font-semibold mb-4">General Details</h3> */}

      <div>
        <FormLabel required>Site Name</FormLabel>
        <FormInput
          {...form.register("site_name")}
          placeholder="Enter Site Name"
          error={form.formState.errors.site_name}
        />
        <FormError>{form.formState.errors.site_name?.message}</FormError>
      </div>

      <div className="flex space-x-2">
        <div className="flex-grow">
          <FormLabel required>Select Local Administrative Body</FormLabel>
          {loadingAdminBodies ? (
            <div className="flex items-center space-x-2">
              <Loader2 className="h-4 w-4 animate-spin" />
              <span className="text-sm text-gray-500">
                Loading admin bodies...
              </span>
            </div>
          ) : (
            <>
              <div className="flex space-x-4">
                <button
                  type="button"
                  onClick={() => {
                    const selected = localAdminBodies.find(
                      (body) => body.admin_body_type === "Municipality"
                    );
                    if (selected) handleAdminBodyChange(selected.id);
                  }}
                  className={`flex flex-col items-center justify-center px-6 py-4 rounded-lg border ${
                    form.watch("local_admin_body_type") === "Municipality"
                      ? "bg-purple-100 border-purple-600"
                      : "bg-gray-100 border-gray-300"
                  }`}
                >
                  {/* <svg className="w-6 h-6 mb-2 text-gray-700" fill="currentColor" viewBox="0 0 24 24"> */}
                  <BsBuilding />
                  {/* </svg> */}
                  Municipality
                </button>

                <button
                  type="button"
                  onClick={() => {
                    const selected = localAdminBodies.find(
                      (body) => body.admin_body_type === "Panchayat"
                    );
                    if (selected) handleAdminBodyChange(selected.id);
                  }}
                  className={`flex flex-col items-center justify-center px-6 py-4 rounded-lg border ${
                    form.watch("local_admin_body_type") === "Panchayat"
                      ? "bg-purple-100 border-purple-600"
                      : "bg-gray-100 border-gray-300"
                  }`}
                >
                  {/* <svg className="w-6 h-6 mb-2 text-gray-700" fill="currentColor" viewBox="0 0 24 24"> */}
                  <IoHomeOutline />
                  {/* </svg> */}
                  Panchayat
                </button>

                <button
                  type="button"
                  onClick={() => {
                    const selected = localAdminBodies.find(
                      (body) => body.admin_body_type === "Other"
                    );
                    if (selected) handleAdminBodyChange(selected.id);
                  }}
                  className={`flex flex-col items-center justify-center px-6 py-4 rounded-lg border ${
                    form.watch("local_admin_body_type") === "Other"
                      ? "bg-purple-100 border-purple-600"
                      : "bg-gray-100 border-gray-300"
                  }`}
                >
                  <MdOutlineMoreHoriz />
                  Other
                </button>
              </div>
              <FormError>
                {form.formState.errors.local_admin_body_id?.message}
              </FormError>
            </>
          )}
        </div>
      </div>
    </div>
  );

  const renderBuildingCategorySelector = () => (
    <div className="space-y-6">
      <h3 className="text-lg font-semibold">Building Category</h3>
      {loadingCategories ? (
        <div className="flex items-center space-x-2">
          <Loader2 className="h-4 w-4 animate-spin" />
          <span className="text-sm text-gray-500">
            Loading building categories...
          </span>
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-4">
          {siteCategories?.map((category) => {
            const isSelected = form.watch("site_catagory_id") === category.id;
            return (
              <div
                key={category.id}
                className={`w-full h-full border rounded-xl px-4 py-3 cursor-pointer transition-all ${
                  isSelected
                    ? "border-purple-600 bg-purple-100 shadow-sm"
                    : "border-gray-200 bg-white hover:bg-gray-50"
                }`}
                onClick={() => form.setValue("site_catagory_id", category.id)}
              >
                <div className="flex justify-between items-start gap-2">
                  <div className="flex gap-3">
                    {/* Placeholder icon - you can use lucide-react or any SVG here */}
                    <div className="p-2 bg-purple-50 text-purple-600 rounded-md">
                      <BsBuilding />
                    </div>
                    <div>
                      <p className="font-medium text-sm text-gray-900">
                        {category.catagory_name}
                      </p>
                      <p className="text-xs text-gray-600">
                        {category.description || ""}
                      </p>
                    </div>
                  </div>
                  {isSelected && (
                    <div className="w-5 h-5 rounded-full bg-purple-600 text-white flex items-center justify-center">
                      <svg
                        className="w-3 h-3"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="3"
                        viewBox="0 0 24 24"
                      >
                        <path d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
      <FormError>{form.formState.errors.site_catagory_id?.message}</FormError>
    </div>
  );

  const renderSiteDetailsForm = () => (
    <div className="space-y-6">
      <h3 className="text-lg font-semibold">Construction Status</h3>

      <div className="grid grid-cols-2 gap-4">
        {/* Starting Date */}
        <div className="flex flex-col">
          <FormLabel required>Starting Date</FormLabel>
          <FormInput
            {...form.register("start_date")}
            type="date"
            className="appearance-none bg-gray-50 border border-gray-300 rounded-md px-3 py-2 text-gray-800"
            error={form.formState.errors.start_date}
          />
          <FormError>{form.formState.errors.start_date?.message}</FormError>
        </div>

        {/* Construction Status */}
        <div className="flex flex-col">
          <FormLabel required className="ml-1">Status</FormLabel>
          <select
            {...form.register("constructionStatus")}
            className="bg-yellow-50 border border-yellow-400 text-yellow-800 font-semibold rounded-md px-3 py-2 appearance-none"
          >
            <option value="Ongoing">Ongoing</option>
            <option value="Upcoming">Upcoming</option>
            <option value="Completed">Completed</option>
            <option value="Halted">Halted</option>
          </select>
          <FormError>
            {form.formState.errors.constructionStatus?.message}
          </FormError>
        </div>
      </div>

      {/* Site Photos */}
      <div className="flex flex-col">
        <FormLabel>Site Photos (Optional)</FormLabel>
        {!previewImage ? (
          <label className="inline-flex items-center justify-center w-fit px-4 py-2 rounded-lg bg-purple-50 text-purple-700 border border-purple-300 cursor-pointer hover:bg-purple-100 transition">
            <Upload className="w-5 h-5 mr-2" />
            Upload Photos
            <input
              type="file"
              accept="image/*"
              className="hidden"
              onChange={handleUploadPhoto}
            />
          </label>
        ) : (
          <div className="relative mt-2">
            <img
              src={previewImage}
              alt="Site Preview"
              className="rounded-lg shadow-md h-48 w-full object-cover"
            />
            <div className="absolute top-2 right-2 flex gap-2">
              <button
                onClick={handleAdjustPhoto}
                className="bg-white text-gray-800 border border-gray-300 px-2 py-1 rounded-md hover:bg-gray-100 text-xs"
              >
                Adjust
              </button>
              <button
                onClick={handleRemoveImage}
                className="bg-red-600 text-white p-1 rounded-full hover:bg-red-700"
                aria-label="Remove image"
              >
                <X size={16} />
              </button>
            </div>
          </div>
        )}
      </div>
      {isCropperOpen && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center">
          <div className="absolute inset-0 bg-black/60" onClick={handleCropCancel} />
          <div className="relative bg-white rounded-lg shadow-xl w-[90vw] max-w-xl h-[60vh] flex flex-col overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
              <h4 className="text-base font-semibold">Adjust Photo</h4>
              <button onClick={handleCropCancel} className="text-gray-500 hover:text-gray-700">✕</button>
            </div>
            <div className="relative flex-1 bg-gray-100 overflow-auto">
              {tempImageSrc && (
                <div className="w-full h-full flex items-center justify-center p-4">
                  <ReactCrop
                    crop={crop}
                    onChange={(c) => setCrop(c)}
                    onComplete={(c) => setCompletedCrop(c)}
                    keepSelection
                    className="max-w-full max-h-full"
                  >
                    <img
                      ref={imgRef}
                      src={tempImageSrc}
                      alt="to-crop"
                      onLoad={onImageLoad}
                      className="max-h-[45vh] max-w-full w-auto h-auto object-contain"
                    />
                  </ReactCrop>
                </div>
              )}
            </div>
            <div className="px-4 py-3 border-t border-gray-200 flex items-center justify-end gap-3">
              <button
                onClick={handleCropCancel}
                className="px-4 py-2 rounded-md border border-gray-300 text-gray-800 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleCropConfirm}
                className="px-4 py-2 rounded-md bg-[#3B0069] text-white hover:bg-purple-700"
              >
                Apply
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  const renderStepContent = () => {
    switch (currentStep) {
      case 0:
        return renderGeneralDetailsForm();

      case 1:
        return renderBuildingCategorySelector();
      case 2:
        return renderSiteDetailsForm();
      default:
        return null;
    }
  };

  const handleSubmit = async () => {
    try {
      const formData = form.getValues();
      await siteSchema.parseAsync(formData);
      createSite.mutate(formData);
    } catch (error) {
      console.error("Validation Error:", error);
      if (error.errors) {
        error.errors.forEach((err) => {
          form.setError(err.path[0], {
            type: "manual",
            message: err.message,
          });
        });
      }
    }
  };

  return (
    <div
      className="w-[600px] h-[600px] mx-auto mt-10 bg-white shadow-lg rounded-lg "
      style={{ overflowY: "scroll" }}
    >
      <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
        <button onClick={handleBack} className="text-gray-600 text-lg">
          ←
        </button>
        <h2 className="text-xl font-semibold">New Site</h2>
        <button onClick={onClose} className="text-gray-600 text-xl">
          ✕
        </button>
      </div>

      <div className="p-6">
        <div className="w-full bg-purple-200 h-2 mb-6 rounded-full">
          <div
            className="h-2 bg-[#3B0069] rounded-full transition-all duration-300 ease-in-out"
            style={{ width: `${((currentStep + 1) / 3) * 100}%` }}
          ></div>
        </div>

        {renderStepContent()}
        <button
          onClick={currentStep === 2 ? handleSubmit : handleNext}
          disabled={isSubmitting}
          className="w-full mt-6 bg-[#3B0069] text-white py-2 px-4 rounded-md hover:bg-purple-700 transition duration-300 flex items-center justify-center text-lg font-medium"
        >
          {isSubmitting ? (
            <>
              <Loader2 className="animate-spin mr-2" size={20} />
              Creating...
            </>
          ) : currentStep === 2 ? (
            <>
              Create
              <svg
                className="ml-2 w-4 h-4"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                viewBox="0 0 24 24"
              >
                <path d="M5 12h14M12 5l7 7-7 7" />
              </svg>
            </>
          ) : (
            <>
              Next
              <svg
                className="ml-2 w-4 h-4"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                viewBox="0 0 24 24"
              >
                <path d="M5 12h14M12 5l7 7-7 7" />
              </svg>
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default NewSiteForm;
